import React from 'react';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import MUIIcon from '../../../styled-base/mui-icon';
import MeetingServices from '../../../services/meeting.services';

class AgendaTemplatesModal extends React.Component {
  static stripHtmlTags(html) {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  constructor(props) {
    super(props);
    this.state = {
      filteredTemplates: [],
      selectedTemplates: [], // Changed to array for multiselect
      searchQuery: '',
      loading: true,
      currentPage: 1,
      totalPages: 1,
      limit: 10,
      previewTemplate: null, // For preview mode
    };
    this.MeetingServices = new MeetingServices();
  }

  componentDidMount() {
    this.fetchTemplates();
  }

  componentWillUnmount() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  }

  fetchTemplates = (page = 1, searchQuery = '') => {
    const { meetingId, t } = this.props;
    const { limit } = this.state;

    this.setState({ loading: true });

    // Call GET /restapi/meetings/:id/templates?available=true with pagination
    this.MeetingServices.getAvailableTemplates(meetingId, page, limit, searchQuery)
      .then((response) => {
        const result = response.data.data || {};
        const templates = result.templates || [];
        const pagination = result.pagination || {};

        this.setState({
          filteredTemplates: templates,
          currentPage: pagination.page || 1,
          totalPages: pagination.totalPages || 1,
          loading: false,
        });
      })
      .catch((error) => {
        console.error('Error fetching templates:', error);
        this.setState({ loading: false });
        toastr.error(t('Failed to load templates'));
      });
  };

  handleSearch = (e) => {
    const searchQuery = e.target.value;

    this.setState({
      searchQuery,
    });

    // Debounce the API call to avoid fetching on every keystroke
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.fetchTemplates(1, searchQuery);
    }, 300);
  };

  handleNextPage = () => {
    const { currentPage, totalPages, searchQuery } = this.state;
    if (currentPage < totalPages) {
      this.fetchTemplates(currentPage + 1, searchQuery);
    }
  };

  handlePreviousPage = () => {
    const { currentPage, searchQuery } = this.state;
    if (currentPage > 1) {
      this.fetchTemplates(currentPage - 1, searchQuery);
    }
  };

  handleTemplateSelect = (template) => {
    this.setState((prevState) => {
      const { selectedTemplates } = prevState;
      const isSelected = selectedTemplates.some((t) => t.id === template.id);
      if (isSelected) {
        return {
          selectedTemplates: selectedTemplates.filter((t) => t.id !== template.id),
        };
      }
      return {
        selectedTemplates: [...selectedTemplates, template],
      };
    });
  };

  handlePreview = (template) => {
    this.setState({ previewTemplate: template });
  };

  handleBackFromPreview = () => {
    this.setState({ previewTemplate: null });
  };

  handleAddTemplate = () => {
    const { selectedTemplates } = this.state;
    const { meetingId, onTemplateAdded, t, onClose } = this.props;

    if (selectedTemplates.length === 0) {
      return;
    }

    // Add all selected templates sequentially
    const addPromises = selectedTemplates.map((template) => this.MeetingServices.addTemplate(meetingId, template.id));

    Promise.all(addPromises)
      .then(() => {
        const count = selectedTemplates.length;
        toastr.success(count > 1 ? t('{{count}} templates applied', { count }) : t('Template applied'));

        // Dispatch custom event to notify AppliedTemplatesDropdown to refresh
        window.dispatchEvent(new CustomEvent('meeting-template-added'));

        if (onTemplateAdded) {
          // Call without data to trigger a full refresh
          onTemplateAdded();
        }
        if (onClose) onClose();
      })
      .catch((error) => {
        const errorMsg = error.response?.data?.message || t('Failed to add template');
        toastr.error(errorMsg);
      });
  };

  renderPreview() {
    const { t, onClose } = this.props;
    const { previewTemplate } = this.state;

    if (!previewTemplate) return null;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Preview Header */}
        <div
          style={{
            padding: '15px 48px',
            borderBottom: '1px solid #E8E8E8',
            position: 'relative',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: 'Lato',
              fontSize: '18px',
              fontWeight: 700,
              color: '#262626',
            }}
          >
            {t('Template Library')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '50%',
              right: '32px',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#687587',
              width: '24px',
              height: '24px',
            }}
            aria-label="Close"
          >
            <CloseIcon style={{ fontSize: 24 }} />
          </button>
        </div>

        {/* Preview Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px 48px 32px',
          }}
        >
          {/* Template Name */}
          <h3
            style={{
              margin: '0 0 16px 0',
              fontFamily: 'Lato',
              fontSize: '20px',
              fontWeight: 700,
              lineHeight: '28px',
              letterSpacing: '0.15px',
              color: '#333943',
            }}
          >
            {previewTemplate.name}
          </h3>

          {/* Description Section */}
          {previewTemplate.description && previewTemplate.description.trim() && (
            <div style={{ marginBottom: '32px' }}>
              <div
                style={{
                  margin: 0,
                  fontFamily: 'Lato',
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  letterSpacing: '0.15%',
                  color: '#000F16D9',
                }}
              >
                {previewTemplate.description}
              </div>
            </div>
          )}

          {/* Discussion Topics Section */}
          {previewTemplate.topics && previewTemplate.topics.length > 0 && (
            <div>
              <h4
                style={{
                  margin: '0 0 12px 0',
                  fontFamily: 'Lato',
                  fontSize: '15px',
                  fontWeight: 400,
                  lineHeight: '150%',
                  letterSpacing: '0.17px',
                  color: '#0A153799',
                }}
              >
                {t('Discussion topics')}
              </h4>
              <div
                style={{
                  margin: 0,
                  padding: 0,
                }}
              >
                {previewTemplate.topics.map((topic) => {
                  const topicText = topic.name || topic.description || topic;
                  const key = topic?.id ?? topic?.name ?? topic?.description ?? String(topic);
                  return (
                    <div
                      key={key}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        marginBottom: '16px',
                        fontFamily: 'Lato',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '156%',
                        letterSpacing: '0.4px',
                        color: '#000F16D9',
                      }}
                    >
                      <span
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '20px',
                          backgroundColor: '#C4CBD7',
                          marginRight: '12px',
                          marginTop: '7px',
                          flexShrink: 0,
                        }}
                      />
                      <span>{AgendaTemplatesModal.stripHtmlTags(topicText)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Preview Footer */}
        <div
          style={{
            padding: '16px 48px 24px',
            borderTop: '1px solid #E8E8E8',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <button
            type="button"
            onClick={this.handleBackFromPreview}
            style={{
              padding: '8px 16px',
              fontFamily: 'Lato',
              fontSize: '14px',
              color: '#0072AB',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {t('Back')}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { t, onClose } = this.props;
    const { filteredTemplates, selectedTemplates, searchQuery, loading, currentPage, totalPages, previewTemplate } =
      this.state;

    return (
      <>
        <style>
          {`
            .agenda-template-search::placeholder {
              font-style: italic !important;
              font-size: 14px !important;
              font-weight: 400 !important;
              color: #687587 !important;
            }
            .agenda-template-search::-webkit-input-placeholder {
              font-style: italic !important;
              font-size: 14px !important;
              font-weight: 400 !important;
              color: #687587 !important;
            }
            .agenda-template-search::-moz-placeholder {
              font-style: italic !important;
              font-size: 14px !important;
              font-weight: 400 !important;
              color: #687587 !important;
            }
            .agenda-template-search:-ms-input-placeholder {
              font-style: italic !important;
              font-size: 14px !important;
              font-weight: 400 !important;
              color: #687587 !important;
            }
            .agenda-template-search:-moz-placeholder {
              font-style: italic !important;
              font-size: 14px !important;
              font-weight: 400 !important;
              color: #687587 !important;
            }
          `}
        </style>
        <div
          className="agenda-templates-modal-overlay"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
          onKeyDown={(e) => {
            if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onClose();
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
        >
          <div
            className="agenda-templates-modal"
            role="dialog"
            aria-modal="true"
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              width: '800px',
              height: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
            }}
          >
            {previewTemplate ? (
              this.renderPreview()
            ) : (
              <>
                {/* Header */}
                <div
                  style={{
                    padding: '15px 48px',
                    position: 'relative',
                    borderBottom: '1px solid #E8E8E8',
                  }}
                >
                  <h2
                    style={{
                      margin: 0,
                      fontFamily: 'Lato',
                      fontSize: '18px',
                      fontWeight: 700,
                      color: '#333943',
                    }}
                  >
                    {t('Select a Template')}
                  </h2>
                  <button
                    type="button"
                    onClick={onClose}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '32px',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#687587',
                      width: '24px',
                      height: '24px',
                    }}
                    aria-label="Close"
                  >
                    <CloseIcon style={{ fontSize: 24 }} />
                  </button>
                </div>

                {/* Search */}
                <div style={{ padding: '24px 48px 24px' }}>
                  <div
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      maxWidth: '350px',
                    }}
                  >
                    <MUIIcon
                      component={SearchIcon}
                      style={{
                        position: 'absolute',
                        left: '16px',
                        fontSize: 20,
                        color: '#8490A3',
                      }}
                    />
                    <input
                      type="text"
                      className="agenda-template-search"
                      placeholder={t('Search by name')}
                      value={searchQuery}
                      onChange={this.handleSearch}
                      style={{
                        width: '100%',
                        padding: '8px 16px 8px 48px',
                        border: '1px solid #8490A3',
                        borderRadius: '24px',
                        fontFamily: 'Lato',
                        fontSize: '14px',
                        outline: 'none',
                      }}
                    />
                  </div>
                </div>

                {/* Template List */}
                <div
                  style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: '0 48px 16px',
                  }}
                >
                  {(() => {
                    if (loading) {
                      return (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#8c9ea2' }}>
                          {t('Loading templates...')}
                        </div>
                      );
                    }
                    if (filteredTemplates.length === 0) {
                      return (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#8c9ea2' }}>
                          {searchQuery ? t('No templates found') : t('No templates available')}
                        </div>
                      );
                    }
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                        {filteredTemplates.map((template) => {
                          const isSelected = selectedTemplates.some((t) => t.id === template.id);
                          const isDisabled = template.is_added === true || template.is_added === 1;
                          const isChecked = isDisabled || isSelected;

                          const handleActivate = () => {
                            if (!isDisabled) {
                              this.handleTemplateSelect(template);
                            }
                          };
                          return (
                            <div
                              key={template.id}
                              role="button"
                              tabIndex={isDisabled ? -1 : 0}
                              onClick={handleActivate}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleActivate();
                                }
                              }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '10px 12px',
                                cursor: isDisabled ? 'default' : 'pointer',
                                backgroundColor: 'white',
                                transition: 'background-color 0.2s',
                                opacity: isDisabled ? 0.5 : 1,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isDisabled}
                                onChange={() => {}}
                                style={{
                                  marginRight: '16px',
                                  cursor: isDisabled ? 'default' : 'pointer',
                                  width: '15px',
                                  height: '15px',
                                  accentColor: '#0072AB',
                                }}
                              />
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontFamily: 'Lato',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    color: isDisabled ? '#999' : '#262626',
                                  }}
                                >
                                  {template.name}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  this.handlePreview(template);
                                }}
                                disabled={isDisabled}
                                style={{
                                  padding: '6px 12px',
                                  fontFamily: 'Lato',
                                  fontSize: '14px',
                                  color: isDisabled ? '#CCC' : '#0072AB',
                                  backgroundColor: 'transparent',
                                  border: 'none',
                                  cursor: isDisabled ? 'default' : 'pointer',
                                  fontWeight: 700,
                                }}
                              >
                                {t('Preview')}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Footer */}
                <div
                  style={{
                    padding: '16px 48px 24px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={this.handlePreviousPage}
                          disabled={currentPage === 1}
                          style={{
                            padding: '4px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            opacity: currentPage === 1 ? 0.5 : 1,
                            color: '#687587',
                          }}
                          aria-label="Previous page"
                        >
                          <ChevronLeftIcon style={{ fontSize: 20 }} />
                        </button>
                        <span style={{ fontFamily: 'Lato', fontSize: '14px' }}>
                          <span style={{ color: '#687587' }}>{currentPage}</span>
                          <span style={{ color: '#687587' }}> / </span>
                          <span style={{ color: '#262626' }}>{totalPages}</span>
                        </span>
                        <button
                          type="button"
                          onClick={this.handleNextPage}
                          disabled={currentPage === totalPages}
                          style={{
                            padding: '4px',
                            backgroundColor: 'transparent',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            opacity: currentPage === totalPages ? 0.5 : 1,
                            color: '#687587',
                          }}
                          aria-label="Next page"
                        >
                          <ChevronRightIcon style={{ fontSize: 20 }} />
                        </button>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={this.handleAddTemplate}
                      disabled={selectedTemplates.length === 0}
                      style={{
                        padding: '10px 32px',
                        fontFamily: 'Lato',
                        fontSize: '14px',
                        color: selectedTemplates.length > 0 ? 'white' : '#999',
                        backgroundColor: selectedTemplates.length > 0 ? '#0072AB' : '#E8E8E8',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: selectedTemplates.length > 0 ? 'pointer' : 'not-allowed',
                        fontWeight: 600,
                      }}
                    >
                      {t('Add Template')}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  }
}

export default AgendaTemplatesModal;
